import { Transition } from "@headlessui/react";
import { Formik } from "formik";
import React from "react";
import { useSelector } from "react-redux";

import { CustomLoader, Modal } from "@app/components";
import {
	type ValidationErrors,
	commonMessages,
	validationErrorOptions,
} from "@app/constants";
import { Button, Input } from "@app/controls";
import type { RootState } from "@app/redux";

import messages from "./messages";
import type { FormValues } from "./models/form";
import type { ViewProperties } from "./properties";

import "./kraken-account-update-modal.css";

export const KrakenAccountUpdateModalView = React.memo(
	(props: ViewProperties) => {
		const { language } = useSelector((state: RootState) => state.language);
		const { saveKrakenDetailsLoading } = useSelector(
			(state: RootState) => state.clients,
		);

		const errorOuterStyle = ["flex", "flex-col", "gap-y-2"].join(" ");

		const errorInnerStyle = [
			"font-primary-regular",
			"kraken-account-update-error",
		].join(" ");

		const getErrors = () => {
			const errors = props.errors || [];

			return (
				<Transition
					show={errors.length > 0}
					enter="transition ease-out duration-300"
					enterFrom="opacity-0 translate-y-5"
					dangerouslySetInnerHTML={{
						__html: `<p>${errors.join("</p><p>")}</p>`,
					}}
				></Transition>
			);
		};

		// #region CONSTANTS

		const defaultValues: FormValues = {};

		// #region COMPONENTS

		const getError = (show: boolean) => {
			const areErrors =
				show ||
				(props.mappedReasons !== undefined &&
					Object.entries(props.mappedReasons).find(
						(x) => x[1] !== undefined,
					) !== undefined);

			return (
				<div className="error pb-5 flex justify-center">
					<span>
						<Transition
							show={areErrors}
							enter="transition ease-out duration-300"
							enterFrom="opacity-0 translate-y-5"
							leave="transition ease-out duration-300"
							leaveTo="opacity-0 -translate-y-5"
						>
							<p>{messages[language].errors.generic}</p>
						</Transition>
					</span>
				</div>
			);
		};

		const getFieldErrorContent = (fieldName: string, localError?: string) => {
			var errors = getFieldErrors(fieldName, localError);
			var showCondition = isFieldInError(fieldName, localError);

			return (
				<Transition
					show={showCondition}
					enter="transition ease-out duration-300"
					leave="transition east-out duration-300"
					enterFrom="opacity-0 translate-y-5"
					enterTo="opacity-100 translate-y-0"
					leaveTo="opacity-0 -translate-y-5"
				>
					{errors && (
						<div className={errorOuterStyle}>
							<div
								className={errorInnerStyle}
								dangerouslySetInnerHTML={{
									__html: `<p>${errors.join("</p><p>")}</p>`,
								}}
							></div>
						</div>
					)}
				</Transition>
			);
		};

		const isFieldInError = (
			key: string,
			localError?: string,
		): boolean | undefined => {
			if (localError) {
				return true;
			}

			var result = false;

			if (props.mappedReasons)
				result = (props.mappedReasons[key] ?? []).length > 0;

			if (!result) return undefined;

			return result;
		};

		const getFieldErrors = (
			key: string,
			localError?: string,
		): string[] | undefined => {
			if (localError) {
				return [mapControlError(localError)];
			}

			var result: string[] = [];

			if (props.mappedReasons) {
				result = props.mappedReasons[key] ?? [];
			}

			if (result.length > 0) {
				return result;
			}

			return undefined;
		};

		const mapControlError = (error: string) => {
			const validationError = error as ValidationErrors;

			if (validationErrorOptions.includes(validationError)) {
				if (validationError && validationError.length > 0) {
					return commonMessages[language].validationError[validationError];
				} else {
					return commonMessages[language].validationError.required;
				}
			} else {
				return commonMessages[language].validationError.required;
			}
		};

		return (
			<>
				{saveKrakenDetailsLoading && <CustomLoader page />}
				<Modal
					disableOverlayOnClose
					className="kraken-account-update-modal h-full"
					open={props.open}
					onClose={props.onClose}
				>
					<div className="kraken-account-update-container">
						<Formik initialValues={defaultValues} onSubmit={props.onFormSubmit}>
							{({ handleSubmit, validateForm, errors }) => (
								<>
									<h1 className="font-primary-bold mb-3">
										{messages[language].header}
									</h1>

									<label className="mb-2">{messages[language].krakenId}</label>
									{getFieldErrorContent("krakenId", errors.krakenId)}
									<Input
										className="mb-3.5 amount-input"
										name="krakenId"
										placeholder={messages[language].krakenId}
										theme="secondary"
										title={messages[language].krakenId}
										type="text"
										required
										useFormik
										validateOnChange={props.validate}
										onChange={() => props.onClearErrors("krakenId")}
									/>

									<label className="mb-2">
										{messages[language].krakenApiKey}
									</label>
									{getFieldErrorContent("krakenApiKey", errors.krakenApiKey)}
									<Input
										className="mb-3.5 amount-input"
										name="krakenApiKey"
										placeholder={messages[language].krakenApiKey}
										theme="secondary"
										title={messages[language].krakenApiKey}
										type="text"
										required
										useFormik
										validateOnChange={props.validate}
										onChange={() => props.onClearErrors("krakenApiKey")}
									/>

									<label className="mb-2">
										{messages[language].krakenApiSecret}
									</label>
									{getFieldErrorContent(
										"krakenApiSecret",
										errors.krakenApiSecret,
									)}
									<Input
										className="mb-3.5 amount-input"
										name="krakenApiSecret"
										placeholder={messages[language].krakenApiSecret}
										theme="secondary"
										title={messages[language].krakenApiSecret}
										type="text"
										required
										useFormik
										validateOnChange={props.validate}
										onChange={() => props.onClearErrors("krakenApiSecret")}
									/>

									<p
										dangerouslySetInnerHTML={{
											__html: messages[language].footer,
										}}
									></p>

									<label className="api-error mb-3">{getErrors()}</label>

									{getError(Object.keys(errors).length > 0)}
									<Button
										className="submit font-primary-medium"
										theme="primary"
										onClick={async () =>
											props.onValidate(await validateForm(), handleSubmit)
										}
									>
										{messages[language].submit}
									</Button>
								</>
							)}
						</Formik>
					</div>
				</Modal>
			</>
		);
	},
);
